import { experienceLabel } from '@dashboard/services/string'
import type { Config, Question } from '@dashboard/types/config'
import { segmentName } from '@shared/services/segmentName'

export const MAX_FILL_TARGETS = 10
export const DEFAULT_FILL_CONFIDENCE = 0.8
export const FILL_CONFIDENCE_PRESETS = [
  { label: 'Very sure', value: 0.9 },
  { label: 'Fairly sure', value: DEFAULT_FILL_CONFIDENCE },
  { label: 'Somewhat sure', value: 0.65 }
]

export function fillConfidenceLabel(value: number): string {
  return FILL_CONFIDENCE_PRESETS.find((preset) => preset.value === value)?.label ?? `${Math.round(value * 100)}% sure`
}

export interface FillTarget {
  dimensionId: string
  label: string
  answers: { segmentId: string; label: string }[]
}

/**
 * Segment groups a text answer can fill, worded as the server words them for Jev: the question that asks
 * the group (else its name), and that question's answer labels (else segment names via `segmentName()`, so
 * the built-in Other answer reads as the question's Other label).
 */
export function fillTargets(config: Config | null | undefined): FillTarget[] {
  const questions = config?.rightAsk?.questions ?? []
  const nameOptions = {
    segmentGroups: config?.dimensions ?? [],
    questions: questions.map((question) => ({
      segmentGroupId: question.dimensionId,
      otherOptions: question.otherOptions
    }))
  }
  return (config?.dimensions ?? []).flatMap((dimension) => {
    const question = questions.find((item) => item.dimensionId === dimension.id)
    const answers = dimension.segments
      .filter((segment) => segment.id !== '$everyone')
      .map((segment) => ({
        segmentId: segment.id,
        label:
          (segment.name !== 'INT_OTHER' &&
            experienceLabel(question?.options.find((option) => option.segmentId === segment.id)?.label)) ||
          experienceLabel(segmentName(segment, nameOptions))
      }))
    if (!answers.length) {
      return []
    }
    return [
      { dimensionId: dimension.id, label: experienceLabel(question?.text) || experienceLabel(dimension.name), answers }
    ]
  })
}

export type FillScore = NonNullable<NonNullable<Question['fill']>['score']>
export type FillScoreRule = FillScore['rules'][number]
export const FILL_SCORE_MIN_POINTS = -100
export const FILL_SCORE_MAX_POINTS = 100
export const MAX_FILL_SCORE_RULES = 10
export const MAX_FILL_SCORE_RULE_CHARS = 300
export const DEFAULT_FILL_SCORE_NAME = 'Lead score'
/** The fill target the server answers for a question's scoring rules; its `segmentId` is a rule ID. */
export const SCORE_FILL_TARGET = '$score'

/** Writes `question.fill` from its parts; with neither targets nor scoring rules the setting is removed. */
export function writeFill(
  question: Question,
  patch: { dimensionIds?: string[]; minConfidence?: number; hints?: Record<string, string[]>; score?: FillScore | null }
): void {
  const dimensionIds = patch.dimensionIds ?? question.fill?.dimensionIds ?? []
  const minConfidence = patch.minConfidence ?? question.fill?.minConfidence ?? DEFAULT_FILL_CONFIDENCE
  const hints = patch.hints ?? question.fill?.hints ?? {}
  const score = patch.score === undefined ? question.fill?.score : patch.score
  if (!dimensionIds.length && !score) {
    delete question.fill
    return
  }
  question.fill = {
    dimensionIds,
    minConfidence,
    ...(Object.keys(hints).length ? { hints } : {}),
    ...(score ? { score } : {})
  }
}

export function formatPoints(points: number): string {
  return points > 0 ? `+${points}` : points < 0 ? `−${Math.abs(points)}` : '0'
}
